import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button, Drawer, IconButton } from "@mui/material";
import {
  Close as CloseIcon,
  InfoIcon as InfoOutlinedIcon,
  LightMode as LightModeOutlinedIcon,
  DarkMode as DarkModeOutlinedIcon,
  ContrastIcon,
} from "components/icons";

import { useMaterialUIController, setBaseColor, setThemeMode } from "context";
import { BASE_COLOR_PRESETS, DEFAULT_BASE_COLOR, buildTokens } from "assets/theme/tokens/palette";
import "./AppearanceDrawer.css";

const THEME_CHOICES = [
  { value: "light", label: "Light", Icon: LightModeOutlinedIcon },
  { value: "dark", label: "Dark", Icon: DarkModeOutlinedIcon },
  { value: "system", label: "System", Icon: ContrastIcon },
];

const PALETTE_PREVIEW = [
  "accent", "accent-hover", "accent-strong", "c3", "c4", "c5",
  "accent-soft", "accent-soft-2", "ink", "success", "warning", "error",
];

const HEX = /^#?([0-9a-f]{6})$/i;

// The preset the Reset button returns to, named on the button.
const DEFAULT_PRESET_NAME =
  BASE_COLOR_PRESETS.find((preset) => preset.color === DEFAULT_BASE_COLOR)?.name ?? "default";

/**
 * Appearance settings for this browser: light, dark or system theme, and the base colour every
 * other colour is generated from (a preset or any custom colour), applied live.
 * @since NEXT_VERSION
 */
export const AppearanceDrawer = ({ open, onClose }) => {
  const [controller, dispatch] = useMaterialUIController();
  const { themeMode = "system", baseColor = DEFAULT_BASE_COLOR, darkMode } = controller;
  // What the panel previews: the applied colour, or one still being picked.
  const [draft, setDraft] = useState(baseColor);
  const [hexText, setHexText] = useState(baseColor);
  const pickerRef = useRef(null);

  // Built here rather than read from the theme, so the panel previews exactly what it will apply.
  const generated = useMemo(() => buildTokens(draft), [draft]);
  const tokens = generated[darkMode ? "dark" : "light"];

  useEffect(() => {
    setDraft(baseColor);
    setHexText(baseColor);
  }, [baseColor]);

  const applyColor = (color) => setBaseColor(dispatch, color.toUpperCase());

  // While the picker is dragged only this panel previews the colour; the app switches once,
  // behind the theme-switch cover, when the picker closes (its native change event).
  useEffect(() => {
    const picker = pickerRef.current;
    if (!picker) return undefined;
    const commit = () => applyColor(picker.value);
    picker.addEventListener("change", commit);
    return () => picker.removeEventListener("change", commit);
  });

  const onPickerInput = (event) => {
    const color = event.target.value.toUpperCase();
    setDraft(color);
    setHexText(color);
  };

  const onHexChange = (event) => {
    const text = event.target.value.trim();
    setHexText(text);
    const match = text.match(HEX);
    if (match) {
      setDraft(`#${match[1]}`.toUpperCase());
      applyColor(`#${match[1]}`);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ width: "auto", whiteSpace: "normal" }}
      slotProps={{ paper: { className: "appearance-drawer", role: "dialog", "aria-labelledby": "appearance-title" } }}
    >
      <header className="appearance-head">
        <div>
          <h2 id="appearance-title" className="appearance-title">
            Appearance
          </h2>
          <p className="appearance-sub">Theme and colours for this browser.</p>
        </div>
        <IconButton className="header-icon-button" aria-label="Close appearance settings" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </header>

      <div className="appearance-body">
        <section className="appearance-field">
          <span className="appearance-label">Theme</span>
          <div className="appearance-seg" role="group" aria-label="Theme">
            {THEME_CHOICES.map(({ value, label, Icon }) => (
              <button
                key={value}
                type="button"
                aria-pressed={themeMode === value}
                onClick={() => setThemeMode(dispatch, value)}
              >
                <Icon fontSize="inherit" />
                {label}
              </button>
            ))}
          </div>
        </section>

        <section className="appearance-field">
          <span className="appearance-label">Base colour</span>
          <div className="appearance-presets" role="group" aria-label="Base colour presets">
            {BASE_COLOR_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className="appearance-preset"
                aria-pressed={baseColor.toUpperCase() === preset.color}
                onClick={() => applyColor(preset.color)}
              >
                <i style={{ background: preset.color }} />
                <span>
                  <b>{preset.name}</b>
                  <small>{preset.color}</small>
                </span>
              </button>
            ))}
          </div>
        </section>

        <section className="appearance-field">
          <span className="appearance-label">Custom colour</span>
          <div className="appearance-custom">
            <input
              type="color"
              className="appearance-picker"
              value={generated.base.toLowerCase()}
              ref={pickerRef}
              onChange={onPickerInput}
              aria-label="Pick a custom base colour"
            />
            <input
              className="appearance-hex"
              value={hexText}
              onChange={onHexChange}
              onBlur={() => setHexText(draft)}
              maxLength={7}
              spellCheck={false}
              aria-label="Base colour hex code"
            />
          </div>
          {generated.adjusted && (
            <p className="appearance-note">
              <InfoOutlinedIcon fontSize="inherit" />
              <span>
                Using <b>{generated.accent}</b>, a darker shade of {generated.base}, so white text on
                buttons stays readable.
              </span>
            </p>
          )}
        </section>

        <section className="appearance-field">
          <span className="appearance-label">Generated palette</span>
          <div className="appearance-swatches">
            {PALETTE_PREVIEW.map((name) => (
              <span
                key={name}
                className="appearance-swatch"
                style={{ background: tokens[name] }}
                title={`--${name}: ${tokens[name]}`}
              />
            ))}
          </div>
        </section>
      </div>

      <footer className="appearance-foot">
        <Button
          variant="contained"
          fullWidth
          disabled={baseColor.toUpperCase() === DEFAULT_BASE_COLOR}
          onClick={() => applyColor(DEFAULT_BASE_COLOR)}
        >
          {`Reset to ${DEFAULT_PRESET_NAME}`}
        </Button>
      </footer>
    </Drawer>
  );
};

AppearanceDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
